import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { Zap, Settings, Menu, X, ArrowRight, ArrowDown, Brain, Lock, Code, Rocket, Users, Globe, ChevronDown, CheckCircle, Star, TrendingUp, Layers, Shield, Cpu, Database, Network, Sparkles } from 'lucide-react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './components/ui/accordion';
import { useToast } from './hooks/use-toast';
import Terms from './pages/Terms';
import Logo from './components/Logo';

const Home = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeTab, setActiveTab] = useState('capabilities');
  const { toast } = useToast();
  const sectionRefs = useRef([]);
  const canvasRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const observers = [];
    sectionRefs.current.forEach((ref, index) => {
      if (!ref) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, index]));
          }
        },
        { threshold: 0.15 }
      );
      observer.observe(ref);
      observers.push(observer);
    });
    return () => observers.forEach(observer => observer.disconnect());
  }, []);

  // Particle animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const particles = [];
    const particleCount = 80;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.opacity = Math.random() * 0.5 + 0.2;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((particle, i) => {
        particle.update();
        particle.draw();
        particles.slice(i + 1).forEach(otherParticle => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 100) {
            ctx.strokeStyle = `rgba(255, 255, 255, ${0.1 * (1 - distance / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.stroke();
          }
        });
      });
      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMenuOpen(false);
    }
  };

  const handleWaitlistSubmit = (e) => {
    e.preventDefault();
    if (!waitlistEmail) return;
    const waitlist = JSON.parse(localStorage.getItem('glaciert_waitlist') || '[]');
    waitlist.push({ email: waitlistEmail, timestamp: new Date().toISOString() });
    localStorage.setItem('glaciert_waitlist', JSON.stringify(waitlist));
    toast({
      title: 'Successfully joined!',
      description: 'You\'re on the waitlist. We\'ll be in touch soon.',
    });
    setWaitlistEmail('');
    setWaitlistOpen(false);
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) return;
    const contacts = JSON.parse(localStorage.getItem('glaciert_contacts') || '[]');
    contacts.push({ ...contactForm, timestamp: new Date().toISOString() });
    localStorage.setItem('glaciert_contacts', JSON.stringify(contacts));
    toast({
      title: 'Message sent!',
      description: 'Thank you for reaching out. We\'ll respond shortly.',
    });
    setContactForm({ name: '', email: '', message: '' });
    setContactOpen(false);
  };

  const parallaxOffset = scrollY * 0.5;
  const heroOpacity = Math.max(0, 1 - scrollY / 500);

  const stats = [
    { label: 'Parameters', value: '405B', icon: Cpu },
    { label: 'Context Window', value: '512K', icon: Database },
    { label: 'Research Papers', value: '150+', icon: Brain },
    { label: 'Global Users', value: '10M+', icon: Globe }
  ];

  const features = [
    {
      icon: Brain,
      title: 'Advanced Reasoning',
      description: 'State-of-the-art logical reasoning and problem-solving capabilities that surpass human-level performance on complex tasks.',
      highlights: ['Multi-step reasoning', 'Abstract thinking', 'Logical deduction']
    },
    {
      icon: Code,
      title: 'Code Generation',
      description: 'Write, debug, and optimize code across 50+ programming languages with expert-level proficiency and best practices.',
      highlights: ['Multi-language support', 'Code optimization', 'Bug detection']
    },
    {
      icon: Globe,
      title: 'Multimodal Understanding',
      description: 'Process and understand text, images, code, and structured data seamlessly in a unified intelligence framework.',
      highlights: ['Vision processing', 'Text analysis', 'Data integration']
    },
    {
      icon: Shield,
      title: 'Safety & Alignment',
      description: 'Built with constitutional AI principles, ensuring responses are helpful, harmless, and aligned with human values.',
      highlights: ['Ethical guidelines', 'Bias mitigation', 'Safe outputs']
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Optimized inference engine delivering responses in milliseconds while maintaining exceptional quality and accuracy.',
      highlights: ['Sub-second latency', 'High throughput', 'Efficient scaling']
    },
    {
      icon: Network,
      title: 'Adaptive Learning',
      description: 'Continuous improvement through reinforcement learning from human feedback and real-world performance metrics.',
      highlights: ['RLHF integration', 'Dynamic updates', 'Performance tuning']
    }
  ];

  const useCases = [
    {
      title: 'Enterprise Solutions',
      description: 'Transform your business operations with AI-powered automation, analytics, and decision support systems.',
      icon: Rocket,
      examples: ['Customer service automation', 'Data analysis & insights', 'Process optimization']
    },
    {
      title: 'Research & Development',
      description: 'Accelerate scientific discovery and innovation with advanced reasoning and hypothesis generation.',
      icon: Sparkles,
      examples: ['Literature review', 'Hypothesis generation', 'Experiment design']
    },
    {
      title: 'Creative Applications',
      description: 'Empower creators with intelligent tools for content generation, editing, and creative exploration.',
      icon: Star,
      examples: ['Content creation', 'Creative writing', 'Design assistance']
    }
  ];

  const benchmarks = [
    { name: 'MMLU (General Knowledge)', score: 96.8, prev: 89.2 },
    { name: 'HumanEval (Coding)', score: 94.3, prev: 87.5 },
    { name: 'GSM8K (Mathematics)', score: 98.1, prev: 92.0 },
    { name: 'BBH (Complex Reasoning)', score: 95.7, prev: 88.3 }
  ];

  return (
    <div className="relative bg-black text-white overflow-x-hidden">
      {/* Animated Canvas Background */}
      <canvas
        ref={canvasRef}
        className="fixed top-0 left-0 w-full h-full pointer-events-none z-0"
        style={{ opacity: 0.4 }}
      />

      {/* Animated Gradient Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div 
          className="absolute w-[800px] h-[800px] rounded-full blur-[120px] opacity-20 animate-float"
          style={{
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 70%)',
            top: '10%',
            left: '10%',
            animation: 'float 20s ease-in-out infinite'
          }}
        />
        <div 
          className="absolute w-[600px] h-[600px] rounded-full blur-[100px] opacity-20 animate-float-delayed"
          style={{
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, transparent 70%)',
            bottom: '20%',
            right: '10%',
            animation: 'float 25s ease-in-out infinite 5s'
          }}
        />
        <div 
          className="absolute w-[700px] h-[700px] rounded-full blur-[110px] opacity-15 animate-float-slow"
          style={{
            background: 'radial-gradient(circle, rgba(236, 72, 153, 0.25) 0%, transparent 70%)',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            animation: 'float 30s ease-in-out infinite 10s'
          }}
        />
      </div>

      {/* Mouse Follow Glow */}
      <div
        className="fixed w-[500px] h-[500px] rounded-full blur-[100px] pointer-events-none z-0 transition-all duration-300 ease-out"
        style={{
          background: 'radial-gradient(circle, rgba(100, 200, 255, 0.08) 0%, transparent 70%)',
          left: mousePosition.x - 250,
          top: mousePosition.y - 250,
        }}
      />

      {/* Header */}
      <header 
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: scrollY > 50 
            ? 'linear-gradient(to bottom, rgba(0, 0, 0, 0.95), rgba(0, 0, 0, 0.85))' 
            : 'linear-gradient(to bottom, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.3))',
          backdropFilter: scrollY > 50 ? 'blur(24px) saturate(180%)' : 'blur(12px) saturate(150%)',
          borderBottom: scrollY > 50 ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid transparent',
          boxShadow: scrollY > 50 ? '0 4px 24px rgba(0, 0, 0, 0.5)' : 'none'
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex justify-between items-center">
          {/* Logo */}
          <Logo 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            asLink={false}
            height={100}
          />
          
          {/* Centered Navigation */}
          <nav className="hidden lg:flex items-center gap-8 absolute left-1/2 transform -translate-x-1/2">
            {[
              { name: 'MISSION', id: 'mission' },
              { name: 'SNOW-1', id: 'snow1' },
              { name: 'FEATURES', id: 'features' },
              { name: 'BENCHMARKS', id: 'benchmarks' },
              { name: 'RESEARCH', id: 'research' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-sm font-medium text-gray-400 hover:text-white transition-colors duration-300 tracking-wider uppercase"
              >
                {item.name}
              </button>
            ))}
          </nav>
          
          {/* Right Side Actions */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <Button
              variant="outline"
              className="hidden sm:flex border-white/20 bg-white/5 hover:bg-white/10 hover:border-white/40 text-white rounded-full px-7 py-5 text-xs tracking-widest transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/20 backdrop-blur-sm"
              onClick={() => setWaitlistOpen(true)}
            >
              TRY GLACIERT
            </Button>
            
            <Button
              variant="outline"
              size="icon"
              className="lg:hidden border-white/30 bg-white/5 hover:bg-white/10 hover:border-white/50 text-white rounded-full w-11 h-11 transition-all duration-300 hover:scale-105"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-black/95 backdrop-blur-2xl z-40 transition-all duration-500 ${menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
        style={{ paddingTop: '80px' }}
      >
        <nav className="flex flex-col items-center justify-center h-full gap-8 text-center">
          {[
            { name: 'Mission', id: 'mission' },
            { name: 'Snow-1', id: 'snow1' },
            { name: 'Features', id: 'features' },
            { name: 'Benchmarks', id: 'benchmarks' },
            { name: 'Use Cases', id: 'usecases' },
            { name: 'Research', id: 'research' },
            { name: 'FAQ', id: 'faq' },
            { name: 'Contact', id: 'contact' }
          ].map((item, idx) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="text-4xl font-light tracking-tight hover:text-blue-400 transition-all duration-300 hover:scale-110"
              style={{
                opacity: menuOpen ? 1 : 0,
                transform: menuOpen ? 'translateY(0)' : 'translateY(20px)',
                transitionDelay: `${idx * 50}ms`
              }}
            >
              {item.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex flex-col justify-center px-6 lg:px-12 pt-32 pb-20 z-10"
        style={{
          transform: `translateY(${parallaxOffset}px)`,
          opacity: heroOpacity
        }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <div className="text-xs tracking-[0.3em] text-gray-400 font-mono mb-12 animate-fadeIn">
            [ MISSION ]
          </div>
          
          <h1 
            className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-extralight tracking-tighter mb-10 leading-[0.95] animate-fadeInUp glow-text-hero"
            style={{ animationDelay: '100ms' }}
          >
            Unlocking<br />Intelligence
          </h1>
          
          <p 
            className="text-xl sm:text-2xl md:text-3xl text-gray-300 font-light max-w-3xl mx-auto mb-8 leading-relaxed animate-fadeInUp"
            style={{ animationDelay: '200ms' }}
          >
            Pushing the boundaries of AI to create systems that are powerful, safe, and aligned with humanity.
          </p>
          <p 
            className="text-lg text-gray-400 font-light max-w-2xl mx-auto mb-16 leading-relaxed animate-fadeInUp"
            style={{ animationDelay: '250ms' }}
          >
            Glaciert represents the next generation of artificial intelligence—combining unprecedented reasoning capabilities with rigorous safety protocols to deliver AI that truly understands and serves human needs.
          </p>
          
          <div 
            className="flex flex-wrap gap-4 mb-16 animate-fadeInUp justify-center"
            style={{ animationDelay: '300ms' }}
          >
            <Button
              variant="outline"
              className="border-white/30 bg-white/5 hover:bg-white/15 hover:border-white/60 text-white rounded-full px-11 py-6 text-sm tracking-widest transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-500/30 group backdrop-blur-sm"
              onClick={() => scrollToSection('snow1')}
            >
              EXPLORE SNOW-1 
              <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Button>
            <Button
              variant="outline"
              className="border-white/30 bg-white/5 hover:bg-white/15 hover:border-white/60 text-white rounded-full px-11 py-6 text-sm tracking-widest transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-purple-500/30 group backdrop-blur-sm"
              onClick={() => setWaitlistOpen(true)}
            >
              JOIN WAITLIST
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 animate-fadeInUp" style={{ animationDelay: '350ms' }}>
            {stats.map((stat, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300 hover:scale-105 text-center">
                <stat.icon className="w-8 h-8 mb-3 text-blue-400 mx-auto" strokeWidth={1.5} />
                <div className="text-3xl font-light mb-1">{stat.value}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
        <button 
          onClick={() => scrollToSection('mission')}
          className="absolute bottom-12 left-6 lg:left-12 text-4xl text-white/20 animate-bounce hover:text-white/40 transition-colors duration-300"
        >
          <ArrowDown />
        </button>
      </section>

      {/* Mission Section */}
      <section 
        id="mission"
        ref={el => sectionRefs.current[0] = el}
        className={`relative min-h-screen flex flex-col justify-center px-6 lg:px-12 py-32 border-t border-white/5 transition-all duration-1000 z-10 ${visibleSections.has(0) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
      >
        <div className="max-w-6xl mx-auto text-center">
          <div className="text-xs tracking-[0.3em] text-gray-400 font-mono mb-12">
            [ OUR VISION ]
          </div>
          
          <h2 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extralight tracking-tighter mb-12 leading-tight glow-text-section">
            Building the future
          </h2>
          
          <div className="space-y-6 text-xl sm:text-2xl text-gray-300 font-light max-w-4xl mx-auto leading-relaxed">
            <p>
              We are a frontier research laboratory focused on developing artificial general intelligence that benefits all of humanity. Our work combines cutting-edge scaling with rigorous safety research to build AI that is reliable and truthful.
            </p>
            <p className="text-lg text-gray-400">
              Founded by leading researchers from top AI institutions, Glaciert is committed to solving the most challenging problems in artificial intelligence—from achieving true reasoning capabilities to ensuring AI systems remain safe, interpretable, and aligned with human values.
            </p>
            <p className="text-lg text-gray-400">
              Our mission extends beyond technological advancement. We believe AI should be a force for good, accessible to all, and designed with transparency at its core. Every model we build undergoes extensive safety testing, bias auditing, and alignment verification before deployment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
            {[
              { icon: Brain, title: 'Innovation First', desc: 'Pioneering new architectures and training methods that push the boundaries of what AI can achieve.' },
              { icon: Shield, title: 'Safety by Design', desc: 'Building safety and alignment into every layer of our systems from the ground up.' },
              { icon: Users, title: 'Open Collaboration', desc: 'Working with the global research community to advance AI for the benefit of everyone.' }
            ].map((item, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:bg-white/10 transition-all duration-300 hover:scale-105 text-center">
                <item.icon className="w-12 h-12 mb-4 text-blue-400 mx-auto" strokeWidth={1.5} />
                <h3 className="text-xl font-light mb-3">{item.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Snow-1 Section */}
      <section 
        id="snow1"
        ref={el => sectionRefs.current[1] = el}
        className={`relative min-h-screen flex flex-col justify-center px-6 lg:px-12 py-32 border-t border-white/5 transition-all duration-1000 z-10 ${visibleSections.has(1) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
      >
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute w-full h-full opacity-10"
            style={{
              background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 70%)'
            }}
          />
        </div>
        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <div className="text-xs tracking-[0.3em] text-gray-400 font-mono mb-12">
            [ SNOW-1 ]
          </div>
          
          <h2 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extralight tracking-tighter mb-8 leading-tight glow-text-section">
            Next-generation<br />intelligence
          </h2>
          <p className="text-xl text-gray-300 font-light max-w-3xl mx-auto mb-20 leading-relaxed">
            Snow-1 represents a quantum leap in AI capabilities. Built on our revolutionary Avalanche architecture, it combines massive scale with unprecedented efficiency, delivering human-level performance across virtually every domain.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: Zap,
                title: 'Capabilities',
                description: 'Snow-1 sets new benchmarks in reasoning, coding, mathematics, and multimodal tasks, surpassing all previous models in key evaluations.',
                details: [
                  'Advanced multi-step reasoning across complex problems',
                  'Expert-level code generation in 50+ languages',
                  'Mathematical proof generation and verification',
                  'Natural language understanding in 100+ languages'
                ]
              },
              {
                icon: Settings,
                title: 'Architecture',
                description: 'Built on our Avalanche hybrid Dense-MoE, with 512k context and native multimodal integration.',
                details: [
                  '405B parameters with sparse activation',
                  '512K token context window for long-form understanding',
                  'Native vision and text processing',
                  'Optimized inference at 1000+ tokens/second'
                ]
              }
            ].map((card, idx) => (
              <div 
                key={idx}
                className="group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-12 lg:p-16 hover:bg-white/10 hover:border-white/20 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-blue-500/10"
                style={{
                  transitionDelay: `${idx * 100}ms`
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <card.icon className="relative w-14 h-14 mb-8 text-white/80 group-hover:text-blue-400 transition-all duration-300 group-hover:scale-110 mx-auto" strokeWidth={1} />
                <h3 className="relative text-3xl font-light tracking-tight mb-5 text-center">{card.title}</h3>
                <p className="relative text-lg text-gray-300 leading-relaxed mb-6 text-center">{card.description}</p>
                <ul className="relative space-y-2">
                  {card.details.map((detail, i) => (
                    <li key={i} className="flex items-start text-sm text-gray-400">
                      <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-blue-400 flex-shrink-0" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section 
        id="features"
        ref={el => sectionRefs.current[2] = el}
        className={`relative min-h-screen flex flex-col justify-center px-6 lg:px-12 py-32 border-t border-white/5 transition-all duration-1000 z-10 ${visibleSections.has(2) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-xs tracking-[0.3em] text-gray-400 font-mono mb-12 text-center">
            [ CORE FEATURES ]
          </div>
          
          <h2 className="text-5xl sm:text-6xl md:text-7xl font-extralight tracking-tighter mb-12 leading-tight glow-text-section text-center">
            Engineered for excellence
          </h2>
          <p className="text-xl text-gray-300 font-light max-w-3xl mx-auto mb-20 leading-relaxed text-center">
            Every feature in Snow-1 has been meticulously designed and tested to deliver unparalleled performance, reliability, and safety.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-white/20 transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/10"
                style={{ transitionDelay: `${idx * 50}ms` }}
              >
                <feature.icon className="w-12 h-12 mb-6 text-white/80 group-hover:text-blue-400 transition-all duration-300 mx-auto" strokeWidth={1.5} />
                <h3 className="text-2xl font-light tracking-tight mb-4 text-center">{feature.title}</h3>
                <p className="text-gray-300 leading-relaxed mb-4 text-center">{feature.description}</p>
                <div className="space-y-2">
                  {feature.highlights.map((highlight, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mr-2" />
                      {highlight}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benchmarks Section */}
      <section 
        id="benchmarks"
        ref={el => sectionRefs.current[3] = el}
        className={`relative min-h-screen flex flex-col justify-center px-6 lg:px-12 py-32 border-t border-white/5 transition-all duration-1000 z-10 ${visibleSections.has(3) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
      >
        <div className="max-w-6xl mx-auto text-center">
          <div className="text-xs tracking-[0.3em] text-gray-400 font-mono mb-12">
            [ PERFORMANCE ]
          </div>
          
          <h2 className="text-5xl sm:text-6xl md:text-7xl font-extralight tracking-tighter mb-12 leading-tight glow-text-section">
            Industry-leading<br />benchmarks
          </h2>
          <p className="text-xl text-gray-300 font-light max-w-3xl mx-auto mb-16 leading-relaxed">
            Snow-1 achieves unprecedented scores across all major AI benchmarks, setting new standards for what's possible in artificial intelligence.
          </p>
          <div className="space-y-6">
            {benchmarks.map((benchmark, idx) => (
              <div 
                key={idx}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:bg-white/10 transition-all duration-300"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="text-left">
                    <h3 className="text-xl font-light mb-1">{benchmark.name}</h3>
                    <p className="text-sm text-gray-400">Previous best: {benchmark.prev}%</p>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-light text-blue-400">{benchmark.score}%</div>
                    <div className="text-sm text-green-400 flex items-center justify-end">
                      <TrendingUp className="w-4 h-4 mr-1" />
                      +{(benchmark.score - benchmark.prev).toFixed(1)}%
                    </div>
                  </div>
                </div>
                <div className="relative w-full h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-1000"
                    style={{ width: visibleSections.has(3) ? `${benchmark.score}%` : '0%' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section 
        id="usecases"
        ref={el => sectionRefs.current[4] = el}
        className={`relative min-h-screen flex flex-col justify-center px-6 lg:px-12 py-32 border-t border-white/5 transition-all duration-1000 z-10 ${visibleSections.has(4) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-xs tracking-[0.3em] text-gray-400 font-mono mb-12 text-center">
            [ APPLICATIONS ]
          </div>
          
          <h2 className="text-5xl sm:text-6xl md:text-7xl font-extralight tracking-tighter mb-12 leading-tight glow-text-section text-center">
            Built for real-world impact
          </h2>
          <p className="text-xl text-gray-300 font-light max-w-3xl mx-auto mb-20 leading-relaxed text-center">
            From enterprise automation to scientific discovery, Snow-1 is transforming how organizations leverage artificial intelligence.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {useCases.map((useCase, idx) => (
              <div 
                key={idx}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-10 hover:bg-white/10 hover:border-white/20 transition-all duration-500 hover:scale-105 hover:shadow-xl hover:shadow-purple-500/10"
              >
                <useCase.icon className="w-14 h-14 mb-6 text-purple-400 mx-auto" strokeWidth={1.5} />
                <h3 className="text-2xl font-light tracking-tight mb-4 text-center">{useCase.title}</h3>
                <p className="text-gray-300 leading-relaxed mb-6 text-center">{useCase.description}</p>
                <div className="space-y-2">
                  {useCase.examples.map((example, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-400">
                      <CheckCircle className="w-4 h-4 mr-2 text-purple-400" />
                      {example}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Section */}
      <section 
        id="research"
        ref={el => sectionRefs.current[5] = el}
        className={`relative min-h-screen flex flex-col justify-center px-6 lg:px-12 py-32 border-t border-white/5 transition-all duration-1000 z-10 ${visibleSections.has(5) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
      >
        <div className="max-w-6xl mx-auto text-center">
          <div className="text-xs tracking-[0.3em] text-gray-400 font-mono mb-12">
            [ RESEARCH ]
          </div>
          
          <h2 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extralight tracking-tighter mb-12 leading-tight glow-text-section">
            Advancing the field
          </h2>
          
          <div className="space-y-6 text-xl text-gray-300 font-light max-w-4xl mx-auto leading-relaxed mb-12">
            <p>
              Our research spans the full spectrum of AI development—from fundamental breakthroughs in model architectures to innovative approaches in alignment and interpretability.
            </p>
            <p className="text-lg text-gray-400">
              We publish our findings openly and collaborate with leading institutions worldwide. Our latest publications cover scalable oversight, mechanistic interpretability, advanced alignment techniques, and novel training methodologies that push the boundaries of what's possible.
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-16">
            <TabsList className="bg-white/5 border border-white/10 justify-center">
              <TabsTrigger value="capabilities" className="data-[state=active]:bg-white/10">Capabilities</TabsTrigger>
              <TabsTrigger value="safety" className="data-[state=active]:bg-white/10">Safety</TabsTrigger>
              <TabsTrigger value="architecture" className="data-[state=active]:bg-white/10">Architecture</TabsTrigger>
            </TabsList>
            <TabsContent value="capabilities" className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { title: 'Scaling Laws for Reasoning', desc: 'New insights into how model scale affects complex reasoning abilities and problem-solving performance.' },
                  { title: 'Multimodal Integration', desc: 'Novel approaches to seamlessly combining vision, language, and code understanding in a unified model.' }
                ].map((paper, idx) => (
                  <div key={idx} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300 text-center">
                    <h3 className="text-xl font-light mb-3">{paper.title}</h3>
                    <p className="text-sm text-gray-400 mb-4">{paper.desc}</p>
                    <Button variant="link" className="text-blue-400 hover:text-blue-300 p-0">
                      Read paper <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="safety" className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { title: 'Constitutional AI at Scale', desc: 'Methods for training large language models to be helpful, harmless, and honest through constitutional principles.' },
                  { title: 'Adversarial Robustness', desc: 'Comprehensive evaluation and improvement of model robustness against adversarial attacks and jailbreaking attempts.' }
                ].map((paper, idx) => (
                  <div key={idx} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300 text-center">
                    <h3 className="text-xl font-light mb-3">{paper.title}</h3>
                    <p className="text-sm text-gray-400 mb-4">{paper.desc}</p>
                    <Button variant="link" className="text-blue-400 hover:text-blue-300 p-0">
                      Read paper <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="architecture" className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { title: 'Avalanche: Hybrid MoE Architecture', desc: 'Our breakthrough Dense-MoE design that combines the benefits of dense and sparse models for optimal performance.' },
                  { title: 'Efficient Long-Context Training', desc: 'Novel techniques for training models with 512K+ context windows without sacrificing quality or speed.' }
                ].map((paper, idx) => (
                  <div key={idx} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300 text-center">
                    <h3 className="text-xl font-light mb-3">{paper.title}</h3>
                    <p className="text-sm text-gray-400 mb-4">{paper.desc}</p>
                    <Button variant="link" className="text-blue-400 hover:text-blue-300 p-0">
                      Read paper <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-center">
            <Button
              variant="outline"
              className="border-white/30 bg-white/5 hover:bg-white/15 hover:border-white/60 text-white rounded-full px-11 py-6 text-sm tracking-widest transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-purple-500/30 group backdrop-blur-sm"
            >
              VIEW ALL PAPERS 
              <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        id="faq"
        ref={el => sectionRefs.current[6] = el}
        className={`relative min-h-screen flex flex-col justify-center px-6 lg:px-12 py-32 border-t border-white/5 transition-all duration-1000 z-10 ${visibleSections.has(6) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-xs tracking-[0.3em] text-gray-400 font-mono mb-12 text-center">
            [ FREQUENTLY ASKED QUESTIONS ]
          </div>
          
          <h2 className="text-5xl sm:text-6xl md:text-7xl font-extralight tracking-tighter mb-12 leading-tight glow-text-section text-center">
            Got questions?
          </h2>
          <p className="text-xl text-gray-300 font-light max-w-2xl mx-auto mb-16 leading-relaxed text-center">
            Find answers to common questions about Snow-1 and Glaciert.
          </p>
          <Accordion type="single" collapsible className="w-full space-y-4">
            <AccordionItem value="item-1" className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-6">
              <AccordionTrigger className="text-lg font-light hover:text-blue-400">
                What makes Snow-1 different from other AI models?
              </AccordionTrigger>
              <AccordionContent className="text-gray-400">
                Snow-1 combines unprecedented scale (405B parameters) with our revolutionary Avalanche hybrid Dense-MoE architecture, delivering superior performance across all benchmarks while maintaining efficiency. Our focus on safety, alignment, and interpretability sets us apart from competitors.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2" className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-6">
              <AccordionTrigger className="text-lg font-light hover:text-blue-400">
                How can I access Snow-1?
              </AccordionTrigger>
              <AccordionContent className="text-gray-400">
                We're currently in private beta. Join our waitlist to get early access to the API. Enterprise customers can contact our team directly for dedicated deployment options and custom solutions.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3" className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-6">
              <AccordionTrigger className="text-lg font-light hover:text-blue-400">
                What safety measures are in place?
              </AccordionTrigger>
              <AccordionContent className="text-gray-400">
                Every Snow-1 model undergoes rigorous safety testing including adversarial robustness evaluation, bias auditing, and alignment verification. We employ constitutional AI principles, RLHF, and continuous monitoring to ensure safe, helpful, and honest outputs.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4" className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-6">
              <AccordionTrigger className="text-lg font-light hover:text-blue-400">
                What are the pricing options?
              </AccordionTrigger>
              <AccordionContent className="text-gray-400">
                We offer flexible pricing based on usage volume, with dedicated options for enterprises. Contact our sales team for custom pricing tailored to your needs. Academic and nonprofit discounts are available.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5" className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-6">
              <AccordionTrigger className="text-lg font-light hover:text-blue-400">
                Can Snow-1 be deployed on-premises?
              </AccordionTrigger>
              <AccordionContent className="text-gray-400">
                Yes, we offer private cloud and on-premises deployment options for enterprise customers with specific security or compliance requirements. Contact our team to discuss your deployment needs.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section 
        id="contact"
        ref={el => sectionRefs.current[7] = el}
        className={`relative min-h-screen flex flex-col justify-center px-6 lg:px-12 py-32 border-t border-white/5 transition-all duration-1000 z-10 ${visibleSections.has(7) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
      >
        <div className="max-w-6xl mx-auto text-center">
          <div className="text-xs tracking-[0.3em] text-gray-400 font-mono mb-12">
            [ GET STARTED ]
          </div>
          
          <h2 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extralight tracking-tighter mb-12 leading-tight glow-text-section">
            Ready to build?
          </h2>
          
          <p className="text-xl text-gray-300 font-light max-w-3xl mx-auto mb-16 leading-relaxed">
            Join thousands of developers and organizations already building the future with Snow-1. Get started today with our API or contact us for enterprise solutions.
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center mb-20">
            <Button
              variant="outline"
              className="border-white/30 bg-white/5 hover:bg-white/15 hover:border-white/60 text-white rounded-full px-11 py-6 text-sm tracking-widest transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-500/30 group backdrop-blur-sm"
              onClick={() => setWaitlistOpen(true)}
            >
              JOIN WAITLIST 
              <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Button>
            <Button
              variant="outline"
              className="border-white/30 bg-white/5 hover:bg-white/15 hover:border-white/60 text-white rounded-full px-11 py-6 text-sm tracking-widest transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-purple-500/30 group backdrop-blur-sm"
              onClick={() => setContactOpen(true)}
            >
              CONTACT SALES
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { icon: Rocket, title: 'Quick Start', desc: 'Get up and running in minutes with our comprehensive documentation and API reference.' },
              { icon: Users, title: 'Support', desc: '24/7 technical support and dedicated account management for enterprise customers.' },
              { icon: Globe, title: 'Community', desc: 'Join our growing community of developers and researchers building with Glaciert.' }
            ].map((item, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:bg-white/10 transition-all duration-300">
                <item.icon className="w-10 h-10 mb-4 text-blue-400 mx-auto" strokeWidth={1.5} />
                <h3 className="text-lg font-light mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-black/50 backdrop-blur-sm border-t border-white/5 px-6 lg:px-12 py-20 text-center z-10">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <Logo asLink={false} height={120} />
          </div>
          <nav className="flex flex-wrap justify-center gap-8 mb-8">
            {['About', 'Research', 'Products', 'Careers', 'Terms', 'Privacy', 'Contact'].map((item) => {
              if (item === 'Terms') {
                return (
                  <Link
                    key={item}
                    to="/terms"
                    className="text-sm text-gray-400 hover:text-white transition-colors duration-300 tracking-wide"
                  >
                    {item}
                  </Link>
                );
              }
              return (
                <button
                  key={item}
                  onClick={() => item === 'Contact' ? scrollToSection('contact') : null}
                  className="text-sm text-gray-400 hover:text-white transition-colors duration-300 tracking-wide"
                >
                  {item}
                </button>
              );
            })}
          </nav>
          <p className="text-sm text-gray-500 font-light tracking-wide">
            &copy; 2025 Glaciert, Inc. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Waitlist Dialog */}
      <Dialog open={waitlistOpen} onOpenChange={setWaitlistOpen}>
        <DialogContent className="bg-black/95 backdrop-blur-2xl border border-white/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light tracking-tight">Join the Waitlist</DialogTitle>
            <DialogDescription className="text-gray-400">
              Be the first to access Snow-1 API when it launches.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleWaitlistSubmit} className="space-y-6 mt-4">
            <Input
              type="email"
              placeholder="your.email@example.com"
              value={waitlistEmail}
              onChange={(e) => setWaitlistEmail(e.target.value)}
              className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-400 focus:ring-blue-400 h-12 backdrop-blur-sm"
              required
            />
            <Button
              type="submit"
              className="w-full bg-white text-black hover:bg-gray-200 rounded-full py-6 tracking-widest transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              JOIN WAITLIST
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="bg-black/95 backdrop-blur-2xl border border-white/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light tracking-tight">Contact Us</DialogTitle>
            <DialogDescription className="text-gray-400">
              Send us a message and we'll get back to you soon.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleContactSubmit} className="space-y-6 mt-4">
            <Input
              type="text"
              placeholder="Your name"
              value={contactForm.name}
              onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
              className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-400 focus:ring-blue-400 h-12 backdrop-blur-sm"
              required
            />
            <Input
              type="email"
              placeholder="your.email@example.com"
              value={contactForm.email}
              onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
              className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-400 focus:ring-blue-400 h-12 backdrop-blur-sm"
              required
            />
            <Textarea
              placeholder="Your message..."
              value={contactForm.message}
              onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
              className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-400 focus:ring-blue-400 min-h-32 backdrop-blur-sm"
              required
            />
            <Button
              type="submit"
              className="w-full bg-white text-black hover:bg-gray-200 rounded-full py-6 tracking-widest transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              SEND MESSAGE
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const App = () => {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/terms" element={<Terms />} />
    </Routes>
  );
};

export default App;
